if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        // Check if we're in predictive search overlay
        const isInPredictiveSearchOverlay = this.closest('.predictive-search-overlay') || 
                                            this.closest('[data-predictive-search-results]') ||
                                            this.closest('.predictive-search-overlay__results') ||
                                            this.closest('.predictive-search-overlay__initial-content');

        // CRITICAL: Always prevent default form submission in overlay
        // Check overlay status first (check formElement after it's defined)
        const formElement = this.form; // Alias for clarity
        const isInOverlay = this.closest('.predictive-search-overlay') || 
                           this.closest('[data-predictive-search-results]') ||
                           this.closest('.predictive-search-overlay__results') ||
                           this.closest('.predictive-search-overlay__initial-content') ||
                           (formElement && formElement.hasAttribute('data-in-overlay'));
        
        if (theme.settings.afterAtc !== 'no-js') {
          // Always prevent default form submission and handle via JavaScript
          this.form.addEventListener('submit', (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            evt.stopImmediatePropagation(); // Also stop immediate propagation
            this.handleSubmit(evt);
            return false; // Extra safety
          }, { capture: true, passive: false }); // Use capture phase and non-passive
          
          // Also prevent default on button click as extra safety
          if (this.submitBtn) {
            // Change button type to prevent default form submission
            if (this.submitBtn.type === 'submit') {
              this.submitBtn.type = 'button'; // Change to button to prevent form submission
            }
            
            this.submitBtn.addEventListener('click', (evt) => {
              evt.preventDefault();
              evt.stopPropagation();
              evt.stopImmediatePropagation();
              // Manually trigger form submit handler
              if (this.form) {
                const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                this.form.dispatchEvent(submitEvent);
              }
              return false;
            }, { capture: true, passive: false });
          }
        } else if (isInOverlay || isInPredictiveSearchOverlay) {
          // Even if afterAtc is 'no-js', prevent navigation in overlay
          this.form.addEventListener('submit', (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            evt.stopImmediatePropagation();
            // In overlay, we still want to add to cart via AJAX
            this.handleSubmit(evt);
            return false;
          }, { capture: true, passive: false });
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      // Opdater evt. skjult _delivery_estimate-felt med den synlige leveringstekst
      this.updateDeliveryEstimateField();

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        // Check if we're in predictive search overlay - if so, NEVER navigate to cart
        // Check multiple possible parent containers and also check if overlay is open
        let isInPredictiveSearchOverlay = false;
        
        // Method 1: Check if product-form is inside overlay container
        if (this.closest('.predictive-search-overlay') || 
            this.closest('[data-predictive-search-results]') ||
            this.closest('.predictive-search-overlay__results') ||
            this.closest('.predictive-search-overlay__initial-content')) {
          isInPredictiveSearchOverlay = true;
        }
        
        // Method 2: Check if overlay is currently open (fallback)
        if (!isInPredictiveSearchOverlay) {
          const openOverlay = document.querySelector('.predictive-search-overlay.is-open') ||
                             document.querySelector('.predictive-search-overlay:not([hidden])');
          if (openOverlay) {
            // Check if this product-form is within the open overlay
            if (openOverlay.contains(this)) {
              isInPredictiveSearchOverlay = true;
            }
          }
        }
        
        // Method 3: Check if form has a data attribute indicating it's in overlay
        const formElement = this.querySelector('.js-product-form');
        if (formElement && formElement.hasAttribute('data-in-overlay')) {
          isInPredictiveSearchOverlay = true;
        }

        // CRITICAL: NEVER navigate to cart if in predictive search overlay
        // Always update cart icon and show success state, but stay in overlay
        if (isInPredictiveSearchOverlay) {
          // In overlay: Update cart icon and open drawer if configured (but don't navigate)
          ProductForm.updateCartIcon(data);
          
          // Open cart drawer if setting is 'drawer' (same behavior as outside overlay)
          if (theme.settings.afterAtc === 'drawer' && this.cartDrawer) {
            setTimeout(() => {
              // Update cart drawer contents and open it
              this.cartDrawer.dataset.justAdded = data.variant_id || '';
              this.cartDrawer.dataset.justAddedNew = !oldCartData.items.some((i) => i.variant_id === data.variant_id);
              this.cartDrawer.renderContents(data, true); // true = open drawer
            }, 700);
          }
          
          if (typeof window !== 'undefined' && window.console && window.console.log) {
            console.log('Product form: Item added in predictive search overlay, NOT navigating to cart');
            console.log('Product form: afterAtc setting:', theme.settings.afterAtc, 'cartDrawer:', !!this.cartDrawer);
          }
        } else if (theme.settings.afterAtc === 'page') {
          // Not in overlay and setting is 'page': Navigate to cart
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Not in overlay and setting is not 'page': Update cart icon and open drawer if configured
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          // Normal behavior - open drawer if configured
          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              // Draweren viser "✓ Lagt i kurven" i headeren ved denne åbning.
              this.cartDrawer.dataset.justAdded = data.variant_id || '';
              this.cartDrawer.dataset.justAddedNew = !oldCartData.items.some((i) => i.variant_id === data.variant_id);
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        ProductForm.maybeWarnSplitFulfillment(this, data.variant_id, newCartData);
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Alerts when cart quantity for a variant exceeds warehouse stock and supplier backorder applies.
     * Uses product-inventory JSON on the product page when present.
     * @param {HTMLElement} productFormEl - product-form element
     * @param {number} variantId - Variant id from cart/add response
     * @param {object} cartData - /cart.js payload
     */
    static maybeWarnSplitFulfillment(productFormEl, variantId, cartData) {
      if (!variantId || !cartData?.items || !theme.strings.cartSplitWarehouseBackorder) return;
      const productInfo = productFormEl.closest('.product-info');
      if (!productInfo) return;
      const productInventory = productInfo.querySelector('product-inventory');
      if (!productInventory || productInventory.dataset.isDigital === 'true') return;
      const jsonEl = productInventory.querySelector('script[type="application/json"]');
      if (!jsonEl) return;
      let variants;
      try {
        variants = JSON.parse(jsonEl.textContent);
      } catch (e) {
        return;
      }
      const variant = variants.find((v) => Number(v.id) === Number(variantId));
      if (!variant) return;
      const inv = Math.max(0, parseInt(variant.inventory_quantity, 10) || 0);
      if (inv <= 0) return;
      if (!variant.supplier_stock) return;
      const vid = Number(variantId);
      const lineQty = cartData.items
        .filter((item) => item.variant_id === vid)
        .reduce((sum, item) => sum + item.quantity, 0);
      if (lineQty <= inv) return;
      const backorderQty = lineQty - inv;
      const msg = String(theme.strings.cartSplitWarehouseBackorder)
        .replace(/\[warehouse_qty\]/g, String(inv))
        .replace(/\[backorder_qty\]/g, String(backorderQty));
      window.alert(msg);
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }

    /**
     * DOM root for delivery copy: same product as this form (avoids document-wide querySelector).
     */
    getDeliveryEstimateSearchRoot() {
      const productInfo = this.closest('.product-info');
      if (productInfo) return productInfo;

      const drawer = this.closest('quick-add-drawer');
      if (drawer && drawer.productEl) return drawer.productEl;

      const card = this.closest('product-card');
      if (card) return card;

      return this.closest('.js-product') || document;
    }

    /**
     * Sets properties[_delivery_estimate] on the form from visible delivery text for the
     * selected variant only (never parent textContent over multi-variant delivery-time markup).
     */
    updateDeliveryEstimateField() {
      if (!this.form) return;
      const input = this.form.querySelector('input[name="properties[_delivery_estimate]"]');
      if (!input) return;

      const variantIdInput = this.form.querySelector('[name="id"]');
      const variantId =
        variantIdInput && variantIdInput.value != null && String(variantIdInput.value).trim() !== ''
          ? String(variantIdInput.value).trim()
          : '';

      const container = this.getDeliveryEstimateSearchRoot();
      // Software: fast lokaliseret tekst ("Direkte download") i stedet for leveringstid.
      const fixedLabel = variantId
        ? container.querySelector(`.js-variant-label[data-variant-id="${variantId}"][data-fixed-delivery-estimate]`)
        : null;
      if (fixedLabel) {
        input.value = fixedLabel.getAttribute('data-fixed-delivery-estimate') || '';
        return;
      }
      let text = '';

      // 1) Fjernlager under fragt — scoped .js-variant-label only (parent .textContent merges all variants).
      if (!text && variantId) {
        const fjernlagerBelow = container.querySelector('.product-inventory__fjernlager-below:not([hidden])');
        if (fjernlagerBelow) {
          const el = fjernlagerBelow.querySelector(
            `.js-variant-label[data-variant-id="${variantId}"]:not(.hidden)`
          );
          if (el) text = el.textContent || '';
        }
      }

      // 2) Backorder/fjernlager notice (delivery-time) — match selected variant
      if (!text && variantId) {
        const el = container.querySelector(
          `.product-inventory__delivery-notice .js-variant-label[data-variant-id="${variantId}"]:not(.hidden)`
        );
        if (el) text = el.textContent || '';
      }

      // Fallback: first visible label in notice (no variant id or markup lag)
      if (!text) {
        const visibleLabel = container.querySelector(
          '.product-inventory__delivery-notice .js-variant-label:not(.hidden)'
        );
        if (visibleLabel) text = visibleLabel.textContent || '';
      }

      // 3) Countdown / in-stock line
      if (!text) {
        const countdownWrapper = container.querySelector('#countdown-wrapper');
        const countdownLine = container.querySelector('#cd_leverings_tid');
        const wrapperVisible = countdownWrapper
          ? window.getComputedStyle(countdownWrapper).display !== 'none'
          : true;
        if (countdownLine && wrapperVisible) {
          text = countdownLine.textContent || '';
        }
      }

      input.value = (text || '').replace(/\s+/g, ' ').trim();
    }
  }

  customElements.define('product-form', ProductForm);
}
